import React, { useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { ReactComponent as TimeIcon } from 'assets/icons/icon-time.svg';
import { ReactComponent as BuildIcon } from 'assets/icons/icon-build.svg';
import {
  Label12Regular,
  Label12Medium,
} from 'style/typography';
import {
  friendlyDateTime,
} from 'lib/utils';
import { asRem } from 'lib/css';
import { FriendlyDateTime } from 'core/DateTimeToggle';
import { SCContext } from 'cms/structureContent/SCContext';
import { AuthorLine } from './AuthorLine';

const VersionRecordsViewWrapper = styled.div`
  .search-bar {
    padding: ${asRem(10)} var(--layout-padding-box);
    border-bottom: 1px solid var(--color-border-1);
    position: sticky;
    top: 0;
    background: var(--color-secondary-bg);
    z-index: 1;

    input {
      width: 100%;
      padding: ${asRem(6)} ${asRem(10)};
      background: var(--color-primary-bg);
      border: 1px solid var(--color-border-2);
      border-radius: ${asRem(4)};
      color: var(--color-text-1);
      font-size: ${asRem(12)};
      font-family: inherit;
      outline: none;

      &:focus {
        border-color: var(--color-button-primary-bg);
      }

      &::placeholder {
        color: var(--color-text-3);
      }
    }
  }

  .no-results {
    padding: ${asRem(20)} var(--layout-padding-box);
    color: var(--color-text-3);
    text-align: center;
  }

  .cs-title {
    padding: 0 var(--layout-padding-box);
  }
  .record {
    padding: 0 var(--layout-padding-box);
    cursor: pointer;
    color: var(--color-label);
    border-left: ${asRem(3)} solid transparent;

    &:hover, &.active {
      color: var(--color-text);
      background-color: var(--color-border-1);
    }

    &.active {
      border-left-color: var(--color-button-primary-bg);
    }

    .record-container {
      padding: ${asRem(14)} 0;
      border-bottom: 1px solid var(--color-border-1);
    }

    .record-item {
      display: flex;
      gap: ${asRem(8)};
      align-items: center;
      padding-bottom: ${asRem(8)};

      svg {
        width: ${asRem(16)};
        height: ${asRem(16)};
      }

      span {
        display: block;
      }
    }

    .stage-dot {
      display: inline-block;
      width: ${asRem(8)};
      height: ${asRem(8)};
      border-radius: 50%;
      flex-shrink: 0;

      &.stage-dot-published {
        background-color: var(--color-published);
      }
      &.stage-dot-draft {
        background-color: var(--color-draft);
      }
      &.stage-dot-versioned {
        background-color: var(--color-text-3);
      }
    }
  }
  .record-container-oneline {
    display: flex;
    gap: ${asRem(4)};
  }

  &.one-line {
    display: flex;
    gap: ${asRem(12)};
    .record {
      padding: 0;
      border-left: none;
      &:hover, &.active {
        color: var(--color-button-primary-bg);
        background-color: inherit;
      }
    }
    .authors {
      padding-left: ${asRem(4)};
    }
  }

  .see-all {
    text-align: right;
    margin-top: ${asRem(12)};
    margin-right: ${asRem(12)};
  }
`;

export function VersionRecordsView({
  resourceDef,
  history,
  resourceKey,
  maxItem = null,
  showReviewLink = true,
  onClick = null,
  selectedKey = null,
  versionKey,
  oneLineView = false,
}) {
  const { sc } = useContext(SCContext);
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const items = useMemo(() => {
    if (!history) return [];
    let result = maxItem !== null
      ? history.records.slice(0, maxItem)
      : [...history.records];
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(({ cs, record }) => {
        const stage = (record.att_stage || '').toLowerCase();
        const action = (record.action || '').toLowerCase();
        const csName = (
          history.columnSetLookup[cs.cs_name]?.name || ''
        ).toLowerCase();
        const authors = (record.authors || [])
          .map((a) => (a.name || '').toLowerCase())
          .join(' ');
        const ts = friendlyDateTime(record.ts).toLowerCase();
        return stage.includes(q)
          || action.includes(q)
          || csName.includes(q)
          || authors.includes(q)
          || ts.includes(q);
      });
    }
    return result;
  }, [history, maxItem, search]);

  if (!history) return null;

  const versionsUrl = resourceDef.pageFor(resourceDef, versionKey);

  const handleOnClick = (item) => {
    if (onClick) {
      onClick(item);
    } else if (versionsUrl) {
      navigate(resourceDef.name === 'StructureContent' && sc
        ? `${versionsUrl.meta.urlFor({ key: resourceKey, sc })}?v=${item.key}`
        : `${versionsUrl.meta.urlFor({ key: resourceKey })}?v=${item.key}`);
    }
  };

  const classNames = ['version-records'];
  if (oneLineView) {
    classNames.push('one-line');
  }

  return (
    <VersionRecordsViewWrapper className={classNames.join(' ')}>
      {!oneLineView && maxItem === null && (
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search versions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      )}
      {items.length === 0 && search.trim() && (
        <div className="no-results">
          <Label12Regular>No matching versions</Label12Regular>
        </div>
      )}
      {items.map(({ key, cs, record }) => (
        <div
          key={key}
          className={`record ${selectedKey === key ? 'active' : ''}`}
          onClick={() => handleOnClick({ key, cs, record })}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleOnClick({ key, cs, record });
            }
          }}
          role="button"
          tabIndex="0"
        >
          <div className={`${oneLineView ? 'record-container-online' : 'record-container'}`}>
            <div className={`record-item stage stage-${record.att_stage || 'unknown'}`}>
              <span className={`stage-dot stage-dot-${(record.att_stage || 'unknown').toLowerCase()}`} />
              <Label12Medium>
                {record.att_stage || 'Unknown'}
                {history.columnSetLookup[cs.cs_name] && history.columnSetLookup[cs.cs_name].name
                  ? ` (${history.columnSetLookup[cs.cs_name].name})`
                  : ''}
              </Label12Medium>
            </div>
            {!oneLineView ? (
              <>
                <div className="record-item action">
                  <BuildIcon />
                  <Label12Regular>{record.action}</Label12Regular>
                </div>
                <div className="record-item time">
                  <TimeIcon />
                  <Label12Regular>{friendlyDateTime(record.ts)}</Label12Regular>
                </div>
                {record?.authors?.map((author) => (
                  <AuthorLine
                    author={author}
                    key={author.key}
                    showTime={record?.authors?.length > 1}
                    className="record-item"
                  />
                ))}
              </>
            )
              : (
                <>
                  {record?.authors && record?.authors.length > 0 && (
                  <>
                    <Label12Regular>{' by '}</Label12Regular>
                    <div className="authors">
                      {record?.authors?.map((author) => (
                        <AuthorLine
                          author={author}
                          key={author.key}
                          showTime={record?.authors?.length > 1}
                          className="record-item"
                        />
                      ))}
                    </div>
                  </>
                  )}
                  <div className="record-item time">
                    <FriendlyDateTime date={record.ts} />
                  </div>
                </>
              )}
          </div>
        </div>
      ))}
      {showReviewLink && versionsUrl && (
        <div className="see-all">
          <Link
            className="plain"
            to={resourceDef.name === 'StructureContent' && sc
              ? versionsUrl.meta.urlFor({ key: resourceKey, sc })
              : versionsUrl.meta.urlFor({ key: resourceKey })}
          >
            Review All & Restore
          </Link>
        </div>
      )}
    </VersionRecordsViewWrapper>
  );
}

VersionRecordsView.propTypes = {
  resourceDef: PropTypes.object,
  history: PropTypes.object,
  resourceKey: PropTypes.any,
  maxItem: PropTypes.number,
  showReviewLink: PropTypes.bool,
  onClick: PropTypes.func,
  selectedKey: PropTypes.string,
  versionKey: PropTypes.string,
  oneLineView: PropTypes.bool,
};
